import React, { forwardRef } from 'react';
import {
  Dimensions,
  ScrollView,
  View,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { defaultScale } from '../../utils';
import { isDesktop } from '../../utils/ScreenSizes';
import { Icon } from '../../components';
import Theme from '../../theme';

interface IProps {
  children: React.ReactNode;
  currentIndex?: number;
  onIndexChange?: (index: number) => void;
  totalItems?: number;
}

const { width } = Dimensions.get('window');
const cardMargin = 12;
const cardMaxWidth = 500;
const cardWidthSmallerDevices = width - defaultScale(cardMargin * 2);

const cardWideWidth = Math.min(cardMaxWidth, cardWidthSmallerDevices);

export const horizontalScrollConstants = {
  CARD_WIDTH: defaultScale(300),
  CARD_HEIGHT: defaultScale(160),
  CARD_WIDTH_WIDE: cardWideWidth,
  CARD_MARGIN: defaultScale(cardMargin),
  CARD_BOARDER_RADIUS: 5,
};

export const HorizontalScroller = forwardRef<ScrollView, IProps>((props, ref) => {
  const {
    children,
    currentIndex = 0,
    onIndexChange,
    totalItems = 0,
  } = props;

  const handleNext = () => {
    if (currentIndex < totalItems - 1) {
      const nextIndex = currentIndex + 1;
      onIndexChange?.(nextIndex);
      (ref as React.RefObject<ScrollView>)?.current?.scrollTo({
        x: nextIndex * horizontalScrollConstants.CARD_WIDTH,
        animated: true,
      });
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      onIndexChange?.(prevIndex);
      (ref as React.RefObject<ScrollView>)?.current?.scrollTo({
        x: prevIndex * horizontalScrollConstants.CARD_WIDTH,
        animated: true,
      });
    }
  };

  const showPrevButton = isDesktop() && currentIndex > 0;
  const showNextButton = isDesktop() && currentIndex < totalItems - 1 && totalItems > 2;

  return (
    <View style={styles.container}>
      {showPrevButton && (
        <TouchableOpacity onPress={handlePrev} style={[styles.navButton, styles.prevButton]}>
          <Icon
            size={defaultScale(30)}
            color={Theme.WHITE}
            name='ios-arrow-back'
          />
        </TouchableOpacity>
      )}
      <ScrollView
        ref={ref}
        horizontal
        contentContainerStyle={{ flexDirection: 'row', height: '100%' }}
        showsHorizontalScrollIndicator={false}
        snapToInterval={horizontalScrollConstants.CARD_WIDTH + horizontalScrollConstants.CARD_MARGIN}
        decelerationRate='fast'
      >
        {children}
      </ScrollView>
      {showNextButton && (
        <TouchableOpacity onPress={handleNext} style={[styles.navButton, styles.nextButton]}>
          <Icon
            size={defaultScale(30)}
            color={Theme.WHITE}
            name='ios-arrow-forward'
          />
        </TouchableOpacity>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    height: horizontalScrollConstants.CARD_HEIGHT,
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -(horizontalScrollConstants.CARD_HEIGHT * 0.15) }],
    zIndex: 2,
    width: defaultScale(30),
    height: horizontalScrollConstants.CARD_HEIGHT * 0.3,
    backgroundColor: Theme.CHARCOAL,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  prevButton: {
    left: defaultScale(12),
  },
  nextButton: {
    right: defaultScale(12),
  },
});
